import { Component } from "@theme/component";

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ["videoSources", "videoElement"];

  connectedCallback() {
    super.connectedCallback();

    const { videoSources, videoElement } = this.refs;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute("src", videoSource);
    }

    videoElement.load();
    videoElement.play().catch(() => {});

    videoElement.addEventListener("playing", this._onPlaying);
    videoElement.addEventListener("pause", this._onPause);
    videoElement.addEventListener("ended", this._onPause);
  }

  disconnectedCallback() {
    const videoElement = this.refs?.videoElement;
    if (videoElement) {
      videoElement.removeEventListener("playing", this._onPlaying);
      videoElement.removeEventListener("pause", this._onPause);
      videoElement.removeEventListener("ended", this._onPause);
    }
    super.disconnectedCallback();
  }

  _onPlaying = () => {
    this.classList.add("is-playing");
  };

  _onPause = () => {
    this.classList.remove("is-playing");
  };
}

if (!customElements.get("video-background-component")) {
  customElements.define("video-background-component", VideoBackgroundComponent);
}
